
import { useState, useMemo } from 'react';
import { toolkits, toolkitCategories, toolkitTypes } from '../../../mocks/toolkits';

export default function ToolkitGrid() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);

  const filteredToolkits = useMemo(() => {
    return toolkits.filter((toolkit) => {
      const matchesSearch =
        searchQuery === '' ||
        toolkit.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        toolkit.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === 'All' || toolkit.category === selectedCategory;
      const matchesType = selectedType === 'All' || toolkit.type === selectedType;
      return matchesSearch && matchesCategory && matchesType;
    });
  }, [searchQuery, selectedCategory, selectedType]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedType('All');
  };

  const hasActiveFilters =
    searchQuery !== '' || selectedCategory !== 'All' || selectedType !== 'All';

  return (
    <section id="explore" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-10">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="Search toolkits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Dropdown */}
          <div className="relative">
            <button
              onClick={() => {
                setCategoryOpen(!categoryOpen);
                setTypeOpen(false);
              }}
              className="flex items-center justify-between w-full md:w-48 px-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="text-gray-700 truncate">
                {selectedCategory === 'All' ? 'Categories' : selectedCategory}
              </span>
              <i
                className={`ri-arrow-down-s-line ml-2 text-gray-400 transition-transform ${
                  categoryOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            {categoryOpen && (
              <div className="absolute top-full left-0 mt-1 w-full md:w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                {toolkitCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setCategoryOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap ${
                      selectedCategory === cat
                        ? 'text-blue-600 font-semibold bg-blue-50'
                        : 'text-gray-700'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Type Dropdown */}
          <div className="relative">
            <button
              onClick={() => {
                setTypeOpen(!typeOpen);
                setCategoryOpen(false);
              }}
              className="flex items-center justify-between w-full md:w-48 px-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="text-gray-700 truncate">
                {selectedType === 'All' ? 'Type' : selectedType}
              </span>
              <i
                className={`ri-arrow-down-s-line ml-2 text-gray-400 transition-transform ${
                  typeOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            {typeOpen && (
              <div className="absolute top-full left-0 mt-1 w-full md:w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                {toolkitTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelectedType(type);
                      setTypeOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap ${
                      selectedType === type
                        ? 'text-blue-600 font-semibold bg-blue-50'
                        : 'text-gray-700'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-4 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line"></i>
              Clear Filters
            </button>
          )}
        </div>

        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-8">
            {selectedCategory !== 'All' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                {selectedCategory}
                <button
                  onClick={() => setSelectedCategory('All')}
                  className="hover:text-blue-900 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </span>
            )}
            {selectedType !== 'All' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                {selectedType}
                <button
                  onClick={() => setSelectedType('All')}
                  className="hover:text-blue-900 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results Count */}
        <p className="text-sm text-gray-500 mb-6">
          Showing {filteredToolkits.length} of {toolkits.length} toolkits
        </p>

        {/* Grid */}
        {filteredToolkits.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredToolkits.map((toolkit) => (
              <div
                key={toolkit.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow group"
              >
                {/* Image */}
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={toolkit.image}
                    alt={toolkit.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="px-2 py-0.5 bg-white/90 text-gray-700 text-xs font-medium rounded">
                      {toolkit.category}
                    </span>
                    <span className="px-2 py-0.5 bg-blue-600/90 text-white text-xs font-medium rounded">
                      {toolkit.type}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    <a
                      href={toolkit.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      {toolkit.title}
                    </a>
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                    {toolkit.description}
                  </p>
                  <a
                    href={toolkit.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap"
                  >
                    VIEW TOOLKIT <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <i className="ri-search-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No toolkits found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
